import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import API, { formatError } from "@/api";
import TaskDetailsDrawer from "@/components/task/TaskDetailsDrawer";
import CreateTaskModal from "@/components/board/CreateTaskModal";
import { toast } from "sonner";
import {
    Plus, Search, AlertTriangle, Clock, Pencil, Trash2, Archive, Check, Copy, BookmarkPlus,
    Users, Briefcase, Code2, Palette, Megaphone, Wrench, Globe, Zap, Target,
    Shield, Rocket, Database, Cpu, Headphones, Star, BarChart2, ClipboardList,
    Calendar, FolderOpen, Layers, Package, Activity, KanbanSquare, FileText, ShoppingCart
} from "lucide-react";

const ENTITY_ICONS = [
    { key: "users", Icon: Users }, { key: "briefcase", Icon: Briefcase },
    { key: "code", Icon: Code2 }, { key: "palette", Icon: Palette },
    { key: "megaphone", Icon: Megaphone }, { key: "wrench", Icon: Wrench },
    { key: "globe", Icon: Globe }, { key: "zap", Icon: Zap },
    { key: "target", Icon: Target }, { key: "shield", Icon: Shield },
    { key: "rocket", Icon: Rocket }, { key: "database", Icon: Database },
    { key: "cpu", Icon: Cpu }, { key: "headphones", Icon: Headphones },
    { key: "star", Icon: Star }, { key: "bar_chart", Icon: BarChart2 },
    { key: "clipboard", Icon: ClipboardList }, { key: "calendar", Icon: Calendar },
    { key: "folder", Icon: FolderOpen }, { key: "layers", Icon: Layers },
    { key: "package", Icon: Package }, { key: "activity", Icon: Activity },
    { key: "kanban", Icon: KanbanSquare }, { key: "file_text", Icon: FileText },
    { key: "shopping_cart", Icon: ShoppingCart },
];

function getEntityIcon(iconKey, DefaultIcon, size = 18) {
    const found = ENTITY_ICONS.find(i => i.key === iconKey);
    const IconComp = (found?.Icon) || DefaultIcon;
    return <IconComp size={size} />;
}

function IconPicker({ value, onChange }) {
    return (
        <div>
            <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-2">Pictogramă</label>
            <div className="grid grid-cols-9 gap-1.5">
                {ENTITY_ICONS.map(({ key, Icon }) => (
                    <button key={key} type="button" onClick={() => onChange(key)}
                        data-testid={`icon-pick-${key}`}
                        className={`w-8 h-8 rounded-md flex items-center justify-center transition-all
                            ${value === key ? "bg-[#002FA7] text-white shadow-sm" : "bg-[#F8F9FA] text-[#6B7280] hover:bg-[#EEF2FF] hover:text-[#002FA7]"}`}>
                        <Icon size={15} />
                    </button>
                ))}
            </div>
        </div>
    );
}

const PRIORITY_CONFIG = {
    critical: { label: "Critic", color: "#EF4444", bg: "#FEF2F2" },
    high: { label: "Ridicat", color: "#F97316", bg: "#FFF7ED" },
    medium: { label: "Mediu", color: "#F59E0B", bg: "#FFFBEB" },
    low: { label: "Scăzut", color: "#6B7280", bg: "#F9FAFB" },
};

function PriorityBadge({ priority }) {
    const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
    return (
        <span className="text-xs font-semibold px-1.5 py-0.5 rounded-sm"
            style={{ color: cfg.color, backgroundColor: cfg.bg }}>
            {cfg.label}
        </span>
    );
}

function TaskCard({ task, index, onOpen }) {
    const isOverdue = task.due_date && new Date(task.due_date) < new Date() && !task.completed_at;
    const isDueSoon = task.due_date && !isOverdue && new Date(task.due_date) < new Date(Date.now() + 2 * 24 * 60 * 60 * 1000);
    return (
        <Draggable draggableId={task.id} index={index}>
            {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                    data-testid={`task-card-${task.id}`}
                    onClick={() => onOpen(task.id)}
                    className={`bg-white border border-[#E4E4E7] rounded-md p-3 cursor-pointer
                        hover:-translate-y-[1px] hover:shadow-md transition-all duration-150
                        ${snapshot.isDragging ? "shadow-lg ring-2 ring-[#002FA7]/20 rotate-1" : ""}`}>
                    {task.labels?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                            {task.labels.slice(0, 3).map(label => (
                                <span key={label.id} className="text-xs px-1.5 py-0.5 rounded-sm font-medium"
                                    style={{ backgroundColor: label.color + "25", color: label.color }}>
                                    {label.name}
                                </span>
                            ))}
                        </div>
                    )}
                    <p className="text-sm font-medium text-[#111827] mb-2 leading-snug">{task.title}</p>
                    <div className="flex items-center justify-between gap-2">
                        <PriorityBadge priority={task.priority} />
                        <div className="flex items-center gap-2">
                            {task.checklist_count > 0 && (
                                <span className="text-xs text-[#9CA3AF]">{task.checklist_completed}/{task.checklist_count}</span>
                            )}
                            {task.due_date && (
                                <span className={`text-xs flex items-center gap-0.5 font-medium
                                    ${isOverdue ? "text-red-600" : isDueSoon ? "text-orange-500" : "text-[#9CA3AF]"}`}>
                                    {isOverdue && <AlertTriangle size={11} />}
                                    {isDueSoon && !isOverdue && <Clock size={11} />}
                                    {new Date(task.due_date).toLocaleDateString("ro-RO", { day: "2-digit", month: "short" })}
                                </span>
                            )}
                            {(() => {
                                const list = task.assignees && task.assignees.length > 0 ? task.assignees : (task.assignee ? [task.assignee] : []);
                                if (list.length === 0) return null;
                                return (
                                    <div className="flex items-center -space-x-1.5 flex-shrink-0" data-testid={`task-${task.id}-assignees`}>
                                        {list.slice(0, 3).map(a => (
                                            <div key={a.id} className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center flex-shrink-0"
                                                style={{ backgroundColor: a.avatar || "#002FA7" }}
                                                title={a.name}>
                                                <span className="text-white text-xs font-bold">{a.name?.[0]}</span>
                                            </div>
                                        ))}
                                        {list.length > 3 && (
                                            <div className="w-6 h-6 rounded-full border-2 border-white bg-[#9CA3AF] flex items-center justify-center flex-shrink-0">
                                                <span className="text-white text-[9px] font-bold">+{list.length - 3}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </Draggable>
    );
}

function ColumnHeader({ column, onAddTask, canCreate, isRestricted }) {
    const colors = { todo: "#6B7280", in_progress: "#3B82F6", review: "#F59E0B", done: "#10B981", standby: "#EF4444", ctc: "#8B5CF6" };
    const color = colors[column.key] || column.color || "#6B7280";
    const isSpecial = column.key === "standby" || column.key === "ctc";
    return (
        <div className="flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                <span className={`text-xs font-bold uppercase tracking-widest ${column.key === "standby" ? "text-[#EF4444]" : column.key === "ctc" ? "text-[#8B5CF6]" : "text-[#111827]"}`}>{column.name}</span>
                <span className="text-xs text-[#9CA3AF] bg-[#F1F3F5] px-1.5 py-0.5 rounded-sm font-semibold">
                    {column.tasks?.length || 0}
                </span>
                {isRestricted && (
                    <span className="text-xs text-[#D1D5DB]" title={isSpecial ? "Doar Manager Departament sau Team Lead pot muta task-uri aici" : "Doar adminii pot muta task-uri aici"}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    </span>
                )}
            </div>
            {canCreate && (
                <button data-testid={`add-task-btn-${column.id}`} onClick={() => onAddTask(column.id)}
                    className="text-[#9CA3AF] hover:text-[#002FA7] hover:bg-[#EEF2FF] rounded p-0.5 transition-colors">
                    <Plus size={15} />
                </button>
            )}
        </div>
    );
}

export default function BoardPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const [board, setBoard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [createTaskColumnId, setCreateTaskColumnId] = useState(null);
    const [filterAssignee, setFilterAssignee] = useState("");
    const [filterPriority, setFilterPriority] = useState("");
    const [search, setSearch] = useState("");
    const [showEditBoard, setShowEditBoard] = useState(false);
    const [editForm, setEditForm] = useState({ name: "", description: "", icon: "kanban", team_ids: [], allow_member_create_tasks: false });
    const [allTeams, setAllTeams] = useState([]);
    const [saving, setSaving] = useState(false);
    const [showDuplicate, setShowDuplicate] = useState(false);
    const [dupForm, setDupForm] = useState({ name: "", include_tasks: false });
    const [showSaveTpl, setShowSaveTpl] = useState(false);
    const [tplForm, setTplForm] = useState({ name: "", description: "" });
    const [ctcToggling, setCtcToggling] = useState(false);
    const canManage = ["super_admin", "admin"].includes(user?.role) || board?.can_manage === true;
    const canMoveToStandby = canManage || !!user?.leads_any_team;

    const hasCtcColumn = useMemo(() => (board?.columns || []).some(c => c.key === "ctc"), [board]);

    const toggleCtcColumn = async () => {
        if (ctcToggling) return;
        const next = !hasCtcColumn;
        setCtcToggling(true);
        try {
            await API.post(`/boards/${id}/ctc-column?enable=${next}`);
            toast.success(next ? "Coloana CTC a fost activată pe acest board" : "Coloana CTC a fost dezactivată");
            fetchBoard();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setCtcToggling(false);
        }
    };

    const canCreateTask = canManage || (board?.can_create_tasks === true) || (board?.allow_member_create_tasks === true);

    const fetchBoard = useCallback(() => {
        API.get(`/boards/${id}`)
            .then(r => {
                setBoard(r.data);
                setEditForm({
                    name: r.data.name,
                    description: r.data.description || "",
                    icon: r.data.icon || "kanban",
                    team_ids: r.data.team_ids || [],
                    allow_member_create_tasks: r.data.allow_member_create_tasks || false,
                });
                // Load teams for workspace (for edit modal)
                if (r.data.workspace_id) {
                    API.get(`/teams?workspace_id=${r.data.workspace_id}`).then(tr => setAllTeams(tr.data)).catch(() => {});
                }
            })
            .catch(() => toast.error("Eroare la încărcarea board-ului"))
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => { fetchBoard(); }, [fetchBoard]);

    // Listen for task deletions to refresh
    useEffect(() => {
        const handler = () => fetchBoard();
        window.addEventListener("task-deleted", handler);
        return () => window.removeEventListener("task-deleted", handler);
    }, [fetchBoard]);

    // Deschide automat taskul dacă există ?task=xxx în URL
    useEffect(() => {
        const tid = searchParams.get("task");
        if (tid) {
            setSelectedTaskId(tid);
        }
    }, [searchParams]);

    const handleDragEnd = async (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;
        const cols = board.columns || [];
        const destColumn = cols.find(c => c.id === destination.droppableId);
        // Restricție Stand By / CTC: doar admin / team lead pot muta aici
        if ((destColumn?.key === "standby" || destColumn?.key === "ctc") && !canMoveToStandby) {
            const label = destColumn?.key === "standby" ? "Stand By" : "Trimis la CTC";
            toast.error(`Doar Manager Departament sau Team Lead pot muta task-uri în ${label}.`);
            return;
        }
        // Member restriction: cannot move to first or last "operational" column (excluding standby/ctc)
        if (!canManage) {
            const firstId = cols[0]?.id;
            const operationalCols = cols.filter(c => c.key !== "standby" && c.key !== "ctc");
            const lastId = operationalCols[operationalCols.length - 1]?.id;
            if (destination.droppableId === firstId || destination.droppableId === lastId) {
                toast.error(`Nu poți muta task-ul în "${destColumn?.name}". Doar administratorii pot face asta.`);
                return;
            }
        }
        const newBoard = JSON.parse(JSON.stringify(board));
        const srcCol = newBoard.columns.find(c => c.id === source.droppableId);
        const dstCol = newBoard.columns.find(c => c.id === destination.droppableId);
        const [moved] = srcCol.tasks.splice(source.index, 1);
        dstCol.tasks.splice(destination.index, 0, moved);
        setBoard(newBoard);
        try {
            await API.post(`/tasks/${draggableId}/move`, { to_column_id: destination.droppableId, new_position: destination.index });
        } catch (err) {
            toast.error(formatError(err));
            fetchBoard();
        }
    };

    const saveBoard = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await API.patch(`/boards/${id}`, {
                workspace_id: board.workspace_id,
                team_ids: editForm.team_ids,
                team_id: editForm.team_ids[0] || "",
                name: editForm.name,
                description: editForm.description,
                icon: editForm.icon,
                allow_member_create_tasks: editForm.allow_member_create_tasks,
            });
            toast.success("Board actualizat");
            setShowEditBoard(false);
            fetchBoard();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const archiveBoard = async () => {
        if (!window.confirm(`Arhivezi board-ul "${board?.name}"? Nu va mai fi vizibil în workspace.`)) return;
        try {
            await API.post(`/boards/${id}/archive`);
            toast.success("Board arhivat");
            navigate(-1);
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const duplicateBoard = async (e) => {
        e?.preventDefault();
        setSaving(true);
        try {
            const res = await API.post(`/boards/${id}/duplicate`, {
                name: dupForm.name.trim() || undefined,
                include_tasks: !!dupForm.include_tasks,
            });
            toast.success("Board duplicat");
            setShowDuplicate(false);
            setDupForm({ name: "", include_tasks: false });
            if (res.data?.id) navigate(`/boards/${res.data.id}`);
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const saveAsTemplate = async (e) => {
        e?.preventDefault();
        if (!tplForm.name.trim()) return;
        setSaving(true);
        try {
            await API.post(`/board-templates`, {
                name: tplForm.name.trim(),
                description: tplForm.description.trim(),
                source_board_id: id,
            });
            toast.success("Șablon salvat. Îl poți folosi când creezi un board nou.");
            setShowSaveTpl(false);
            setTplForm({ name: "", description: "" });
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const getFilteredTasks = (tasks) => {
        return (tasks || []).filter(t => {
            if (filterAssignee) {
                const aids = t.assignee_ids || (t.assignee?.id ? [t.assignee.id] : []);
                if (!aids.includes(filterAssignee)) return false;
            }
            if (filterPriority && t.priority !== filterPriority) return false;
            if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false;
            return true;
        });
    };

    const allAssignees = [];
    const seenIds = new Set();
    board?.columns?.forEach(col => col.tasks?.forEach(t => {
        if (t.assignees && t.assignees.length > 0) {
            t.assignees.forEach(a => {
                if (!seenIds.has(a.id)) { seenIds.add(a.id); allAssignees.push(a); }
            });
        } else if (t.assignee && !seenIds.has(t.assignee.id)) {
            seenIds.add(t.assignee.id);
            allAssignees.push(t.assignee);
        }
    }));

    if (loading) return (
        <div className="p-6 flex gap-4">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="w-72 h-96 bg-white border border-[#E4E4E7] rounded-md animate-pulse flex-shrink-0" />
            ))}
        </div>
    );

    if (!board) return <div className="p-6 text-center text-gray-500">Board negăsit</div>;

    return (
        <div className="h-full flex flex-col" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {/* Board Header */}
            <div className="px-6 py-4 bg-white border-b border-[#E4E4E7] flex-shrink-0">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-50 rounded-md flex items-center justify-center text-[#002FA7] flex-shrink-0">
                            {getEntityIcon(board.icon, KanbanSquare, 17)}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-lg font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                    {board.name}
                                </h1>
                                {canManage && (
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => setShowEditBoard(true)} data-testid="edit-board-btn"
                                            className="text-[#9CA3AF] hover:text-[#002FA7] p-1 rounded hover:bg-blue-50 transition-colors"
                                            title="Editează board">
                                            <Pencil size={13} />
                                        </button>
                                        <button onClick={() => { setDupForm({ name: `${board.name} (copie)`, include_tasks: false }); setShowDuplicate(true); }}
                                            data-testid="duplicate-board-btn"
                                            className="text-[#9CA3AF] hover:text-[#002FA7] p-1 rounded hover:bg-blue-50 transition-colors"
                                            title="Duplică board">
                                            <Copy size={13} />
                                        </button>
                                        <button onClick={() => { setTplForm({ name: board.name, description: "" }); setShowSaveTpl(true); }}
                                            data-testid="save-template-btn"
                                            className="text-[#9CA3AF] hover:text-[#10B981] p-1 rounded hover:bg-green-50 transition-colors"
                                            title="Salvează ca șablon">
                                            <BookmarkPlus size={13} />
                                        </button>
                                        <button onClick={archiveBoard} data-testid="archive-board-btn"
                                            className="text-[#9CA3AF] hover:text-orange-500 p-1 rounded hover:bg-orange-50 transition-colors"
                                            title="Arhivează board">
                                            <Archive size={13} />
                                        </button>
                                    </div>
                                )}
                            </div>
                            {board.description && <p className="text-xs text-[#9CA3AF] mt-0.5">{board.description}</p>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative">
                            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                            <input data-testid="board-search" value={search} onChange={e => setSearch(e.target.value)}
                                placeholder="Caută taskuri..."
                                className="pl-8 pr-3 py-1.5 border border-[#E4E4E7] rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-[#002FA7] w-44" />
                        </div>
                        <select data-testid="filter-priority" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
                            className="px-2.5 py-1.5 border border-[#E4E4E7] rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-[#002FA7] bg-white">
                            <option value="">Toate prioritățile</option>
                            <option value="critical">Critic</option>
                            <option value="high">Ridicat</option>
                            <option value="medium">Mediu</option>
                            <option value="low">Scăzut</option>
                        </select>
                        {allAssignees.length > 0 && (
                            <select data-testid="filter-assignee" value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}
                                className="px-2.5 py-1.5 border border-[#E4E4E7] rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-[#002FA7] bg-white">
                                <option value="">Toți membrii</option>
                                {allAssignees.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                        )}
                        {canCreateTask && (
                            <button data-testid="add-task-top-btn" onClick={() => setCreateTaskColumnId(board.columns[0]?.id)}
                                className="flex items-center gap-1.5 bg-[#002FA7] text-white text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-[#002482] transition-colors">
                                <Plus size={14} /> Task nou
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Kanban Board */}
            <div className="flex-1 overflow-x-auto p-6">
                <DragDropContext onDragEnd={handleDragEnd}>
                    <div className="flex gap-5 h-full items-start" style={{ minWidth: `${(board.columns?.length || 1) * 300}px` }}>
                        {board.columns?.map((column, colIdx) => {
                            const isFirst = colIdx === 0;
                            const operationalCols = (board.columns || []).filter(c => c.key !== "standby" && c.key !== "ctc");
                            const isLastOperational = column.id === operationalCols[operationalCols.length - 1]?.id;
                            const isStandby = column.key === "standby";
                            const isCtc = column.key === "ctc";
                            const isSpecialRestricted = (isStandby || isCtc) && !canMoveToStandby;
                            const filteredTasks = getFilteredTasks(column.tasks);
                            return (
                                <div key={column.id} className="w-72 flex-shrink-0 flex flex-col max-h-full" data-testid={`column-${column.id}`}>
                                    <ColumnHeader column={column} onAddTask={setCreateTaskColumnId}
                                        canCreate={canCreateTask && !((isStandby || isCtc) && !canMoveToStandby)}
                                        isRestricted={(!canManage && (isFirst || isLastOperational)) || isSpecialRestricted} />
                                    <Droppable droppableId={column.id} isDropDisabled={isSpecialRestricted}>
                                        {(provided, snapshot) => (
                                            <div ref={provided.innerRef} {...provided.droppableProps}
                                                data-testid={`droppable-${column.id}`}
                                                className={`flex-1 rounded-md p-2 space-y-2 min-h-[100px] transition-colors overflow-y-auto
                                                    ${snapshot.isDraggingOver
                                                        ? (isStandby ? "bg-[#FEE2E2] border-2 border-dashed border-[#EF4444]"
                                                            : isCtc ? "bg-[#EDE9FE] border-2 border-dashed border-[#8B5CF6]"
                                                                : "bg-[#EEF2FF] border-2 border-dashed border-[#002FA7]")
                                                        : (isStandby ? "bg-[#FEF2F2] border border-[#FECACA]"
                                                            : isCtc ? "bg-[#FAF5FF] border border-[#E9D5FF]"
                                                                : "bg-[#F8F9FA]")}`}
                                                style={{ maxHeight: "calc(100vh - 220px)" }}>
                                                {filteredTasks.map((task, index) => (
                                                    <TaskCard key={task.id} task={task} index={index} onOpen={setSelectedTaskId} />
                                                ))}
                                                {provided.placeholder}
                                                {filteredTasks.length === 0 && !snapshot.isDraggingOver && (
                                                    <div className="text-center py-8 text-xs text-[#9CA3AF]">
                                                        Trage un task aici sau apasă +
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            );
                        })}
                    </div>
                </DragDropContext>
            </div>

            {/* Edit Board Modal */}
            {canManage && showEditBoard && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setShowEditBoard(false)} />
                    <div className="relative bg-white rounded-md shadow-xl w-full max-w-md mx-4 z-10">
                        <div className="px-5 py-4 border-b border-[#E4E4E7]">
                            <h2 className="font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Editează board</h2>
                        </div>
                        <form onSubmit={saveBoard} className="px-5 py-4 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Nume *</label>
                                <input autoFocus data-testid="edit-board-name" value={editForm.name}
                                    onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" required />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Descriere</label>
                                <input data-testid="edit-board-description" value={editForm.description}
                                    onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                            </div>
                            <IconPicker value={editForm.icon} onChange={icon => setEditForm({ ...editForm, icon })} />
                            {allTeams.length > 0 && (
                                <div>
                                    <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Echipe</label>
                                    <div className="space-y-1.5 max-h-28 overflow-y-auto rounded-md border border-[#E4E4E7] p-2">
                                        {allTeams.map(t => (
                                            <label key={t.id} className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox"
                                                    checked={editForm.team_ids.includes(t.id)}
                                                    onChange={e => {
                                                        if (e.target.checked) {
                                                            setEditForm({ ...editForm, team_ids: [...editForm.team_ids, t.id] });
                                                        } else {
                                                            setEditForm({ ...editForm, team_ids: editForm.team_ids.filter(id => id !== t.id) });
                                                        }
                                                    }}
                                                    className="w-3.5 h-3.5 accent-[#002FA7]"
                                                    data-testid={`edit-board-team-${t.id}`}
                                                />
                                                <span className="text-sm text-[#111827]">{t.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* Member permissions */}
                            <div className="border-t border-[#F1F3F5] pt-4">
                                <p className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wide mb-3">Permisiuni Membri</p>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-semibold text-[#111827]">Permite membrilor să creeze task-uri</p>
                                        <p className="text-xs text-[#9CA3AF] mt-0.5">Dacă dezactivat, doar adminii pot crea task-uri</p>
                                    </div>
                                    <button type="button"
                                        data-testid="toggle-member-create-tasks"
                                        onClick={() => setEditForm({ ...editForm, allow_member_create_tasks: !editForm.allow_member_create_tasks })}
                                        className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 ${editForm.allow_member_create_tasks ? "bg-[#002FA7]" : "bg-[#E4E4E7]"}`}>
                                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${editForm.allow_member_create_tasks ? "translate-x-5" : "translate-x-0.5"}`} />
                                    </button>
                                </div>
                            </div>
                            <div className="border-t border-[#F1F3F5] pt-4">
                                <p className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wide mb-3">Coloane speciale</p>
                                <div className="flex items-center justify-between">
                                    <div className="flex-1 pr-3">
                                        <p className="text-sm font-semibold text-[#111827] flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-[#8B5CF6]" />
                                            Coloana „Trimis la CTC"
                                        </p>
                                        <p className="text-xs text-[#9CA3AF] mt-0.5">Activează doar pe board-urile unde echipa de design trimite la control tehnic de calitate.</p>
                                    </div>
                                    <button type="button"
                                        data-testid="toggle-ctc-column"
                                        disabled={ctcToggling}
                                        onClick={toggleCtcColumn}
                                        className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 disabled:opacity-50 ${hasCtcColumn ? "bg-[#8B5CF6]" : "bg-[#E4E4E7]"}`}>
                                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${hasCtcColumn ? "translate-x-5" : "translate-x-0.5"}`} />
                                    </button>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setShowEditBoard(false)} className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                                <button data-testid="save-board-btn" type="submit" disabled={saving}
                                    className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {saving ? "Se salvează..." : "Salvează"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Task Details Drawer */}
            {selectedTaskId && (
                <TaskDetailsDrawer taskId={selectedTaskId} onClose={() => setSelectedTaskId(null)} onUpdate={fetchBoard} />
            )}

            {/* Create Task Modal */}
            {createTaskColumnId && (
                <CreateTaskModal boardId={id} columnId={createTaskColumnId} teamId={board.team_id}
                    onClose={() => setCreateTaskColumnId(null)} onCreated={fetchBoard} />
            )}

            {/* Duplicate Board Modal */}
            {showDuplicate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setShowDuplicate(false)} />
                    <form onSubmit={duplicateBoard}
                        data-testid="duplicate-board-modal"
                        className="relative bg-white rounded-md shadow-xl w-full max-w-md mx-4 z-10">
                        <div className="px-5 py-4 border-b border-[#E4E4E7]">
                            <h2 className="font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Duplică board</h2>
                            <p className="text-xs text-[#6B7280] mt-1">Coloanele, etichetele și (opțional) task-urile vor fi copiate într-un board nou.</p>
                        </div>
                        <div className="px-5 py-4 space-y-3">
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Nume board nou</label>
                                <input data-testid="dup-name-input" value={dupForm.name} onChange={e => setDupForm({ ...dupForm, name: e.target.value })}
                                    autoFocus
                                    placeholder="ex: Marketing Q2 (copie)"
                                    className="w-full px-2.5 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" data-testid="dup-include-tasks" checked={dupForm.include_tasks}
                                    onChange={e => setDupForm({ ...dupForm, include_tasks: e.target.checked })}
                                    className="w-4 h-4 accent-[#002FA7]" />
                                <span className="text-sm text-[#4B5563]">Copiază și task-urile (fără atașamente / comentarii / istoric)</span>
                            </label>
                        </div>
                        <div className="flex gap-2 px-5 py-4 border-t border-[#E4E4E7]">
                            <button type="button" onClick={() => setShowDuplicate(false)}
                                className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                            <button type="submit" data-testid="dup-submit" disabled={saving}
                                className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                {saving ? "Se duplică..." : "Duplică"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Save as Template Modal */}
            {showSaveTpl && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setShowSaveTpl(false)} />
                    <form onSubmit={saveAsTemplate}
                        data-testid="save-template-modal"
                        className="relative bg-white rounded-md shadow-xl w-full max-w-md mx-4 z-10">
                        <div className="px-5 py-4 border-b border-[#E4E4E7]">
                            <h2 className="font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Salvează board ca șablon</h2>
                            <p className="text-xs text-[#6B7280] mt-1">Vor fi salvate coloanele, etichetele, task-urile (cu responsabili și checklist-uri). NU se salvează atașamentele, comentariile sau istoricul.</p>
                        </div>
                        <div className="px-5 py-4 space-y-3">
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Nume șablon</label>
                                <input data-testid="tpl-name-input" value={tplForm.name} onChange={e => setTplForm({ ...tplForm, name: e.target.value })}
                                    autoFocus required
                                    placeholder="ex: Kanban Marketing Standard"
                                    className="w-full px-2.5 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Descriere (opțional)</label>
                                <textarea data-testid="tpl-desc-input" value={tplForm.description} onChange={e => setTplForm({ ...tplForm, description: e.target.value })}
                                    rows={2}
                                    placeholder="Pentru ce campanii folosim acest șablon"
                                    className="w-full px-2.5 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-none" />
                            </div>
                        </div>
                        <div className="flex gap-2 px-5 py-4 border-t border-[#E4E4E7]">
                            <button type="button" onClick={() => setShowSaveTpl(false)}
                                className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA]">Anulează</button>
                            <button type="submit" data-testid="tpl-submit" disabled={saving || !tplForm.name.trim()}
                                className="flex-1 bg-[#10B981] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#0EA371] disabled:opacity-60">
                                {saving ? "Se salvează..." : "Salvează șablon"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
